import {
  MetamaskIcon,
  MoongateIconColor,
  PhantomIcon,
  WalletConnectIcon,
} from "@assets";
import { Input } from "@chakra-ui/react";
import { to } from "@lib/utils";
import { normalizeEmail } from "@models/utils";
import { Wallet } from "@models/wallet";
import * as Sentry from "@sentry/browser";
import Image from "next/image";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import LoadingAnimation from "../../components/Loader/LoadingAnimation";
import { sendSignInEmail, signInByWallet, useUserStore } from "./auth/services";
import VerifyEmail from "./VerifyEmail";
import ConnectWallet from "./wallet/ConnectWallet";
import { WalletExtension } from "./wallet/services";

const SignInModal = ({
  walletLoginMethods: walletLoginMethods = [],
  ssoLoginMethods: ssoLoginMethods = [],
  onAuthenticated: onAuthenticated = () => {},
}: {
  walletLoginMethods?: WalletExtension[];
  ssoLoginMethods?: string[];
  onAuthenticated?: () => void;
}) => {
  const user = useUserStore((state) => state.user);
  const authenticating = useUserStore((state) => state.authenticating);

  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [_email, setEmail] = useState("");
  const [error, setError] = useState("");

  const loginMethods = useMemo(
    () => [...ssoLoginMethods, ...walletLoginMethods],
    [ssoLoginMethods, walletLoginMethods]
  );

  const showExtraMethods = useMemo(
    () => loginMethods.length > 0,
    [loginMethods]
  );

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();

  useEffect(() => {
    if (!user) return;
    onAuthenticated();
  }, [user]);

  const _signInByWallet = async (wallet: Wallet) => {
    if (authenticating || !wallet?.address) return;
    Sentry.addBreadcrumb({
      category: "signInForm:signInByWallet",
      level: "info",
    });
    Sentry.setTag("wallet.address", wallet.address);
    Sentry.setTag("wallet.network", wallet.network);
    console.log("_signInByWallet", wallet);
    setLoading(true);
    const { res: user } = await to(signInByWallet(wallet));
    console.log("_signInByWallet", user);
    setTimeout(() => setLoading(false), 500);
  };

  const _signInByEmail = async (email: string) => {
    if (authenticating || !email) return;
    Sentry.addBreadcrumb({
      category: "signInForm:signInByEmail",
      level: "info",
    });
    Sentry.setTag("email", email);
    email = normalizeEmail(email);
    setEmail(email);
    console.log("_signInByWallet", email);
    setLoading(true);
    const { err: emailErr, res: _sent } = await to(sendSignInEmail(email));
    if (emailErr || !_sent) {
      console.error(emailErr);
      setError(String(emailErr || ""));
    } else {
      setSent(true);
    }
    setLoading(false);
  };

  const WalletLoginButton = ({ extension }: { extension: WalletExtension }) => {
    const Icon = () => {
      switch (extension) {
        case WalletExtension.METAMASK:
          return MetamaskIcon;
        case WalletExtension.PHANTOM:
          return PhantomIcon;
        default:
          return WalletConnectIcon;
      }
    };
    return (
      <div className="flex flex-row justify-center items-center bg-glass-flat hover:bg-glass-green rounded-lg border border-neutral-600 cursor-pointer hover:border-primary h-14 w-14 relative mx-2">
        <ConnectWallet extension={extension} onSuccess={_signInByWallet}>
          <div className="w-8">
            <Image src={Icon()} alt="" layout="responsive"></Image>
          </div>
        </ConnectWallet>
      </div>
    );
  };

  if (user)
    return (
      <div className="flex flex-row justify-center p-16 m-auto">
        <p className="text-primary capitalized">
          You've logged in successfully.
        </p>
      </div>
    );

  return (
    <div className="p-8 sm:py-16">
      {loading && (
        <div className="flex flex-row justify-center p-16">
          <LoadingAnimation />
        </div>
      )}

      {!loading && sent && (
        <VerifyEmail email={_email} resend={() => _signInByEmail(_email)} />
      )}

      {!loading && !user && error && (
        <div className="flex flex-col items-center justify-center m-8 text-center">
          <p className="my-8">{error}</p>
          <a className="text-primary" href="/">
            Back
          </a>
        </div>
      )}

      {!loading && !sent && !error && (
        <div className="sm:max-w-md min-w-fit">
          <div className="flex flex-col items-center justify-center mb-8 text-center">
            <div className="w-12 mb-8 svg-primary">
              <Image
                src={MoongateIconColor}
                alt="moongate"
                layout="responsive"
              ></Image>
            </div>
            <p className="text-2xl font-semibold">Welcome to Moongate</p>
            <p className="text-base my-4">Log in or sign up</p>
          </div>

          <div className="flex flex-col items-center justify-center">
            <form
              onSubmit={handleSubmit((data) => {
                _signInByEmail(data.email);
              })}
            >
              <Input
                className=" bg-glass-flat rounded-lg w-full border border-neutral-600 cursor-pointer hover:border-primary hover:text-primary"
                placeholder="Email address"
                padding="4"
                {...register("email", { required: true })}
              />
              <button
                className="mt-4 bg-glass-green rounded-lg p-4 border cursor-pointer border-primary text-primary text-sm font-semibold capitalize justify-center w-full hover:opacity-80"
                type="submit"
              >
                Let&#39;s Get Started
              </button>
            </form>

            {showExtraMethods && (
              <>
                <div className="relative flex flex-row my-10 h-fit w-full">
                  <span className="flex-grow border-t border-neutral-400 h-full mt-2"></span>
                  <span className="flex-shrink-0 mx-4 text-secondary capitalize text-sm">
                    Or Continue With
                  </span>
                  <span className="flex-grow border-t border-neutral-400 h-full mt-2"></span>
                </div>

                <div className="flex flex-row flex-wrap items-center justify-center w-fit">
                  {walletLoginMethods.map((extension, i) => (
                    <WalletLoginButton key={i} extension={extension} />
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SignInModal;
